import React from 'react';
import { AlertCircle, ArrowLeft, ChevronDown, Flame, MapPin, Phone, Shield, Stethoscope, Wrench, type LucideIcon } from 'lucide-react';
import {
  NATIONAL_CONTACTS,
  ROUTE_68_CONCESSIONAIRE,
  ROUTE_68_SOS_NOTE,
  PRIVATE_ASSISTANCE,
  EMERGENCY_COMUNAS,
  EMERGENCY_DATA_VERIFIED_AT,
  type EmergencyContact,
} from '../lib/emergencyData';

interface EmergencyPageProps {
  onBack: () => void;
}

const NATIONAL_ICONS: Record<string, LucideIcon> = {
  Carabineros: Shield,
  SAMU: Stethoscope,
  Bomberos: Flame,
};

const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

const ContactCard: React.FC<{ contact: EmergencyContact; color: string; Icon: LucideIcon }> = ({
  contact,
  color,
  Icon,
}) => (
  <div className={`rounded-2xl border p-5 ${color}`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center shrink-0">
        <Icon size={22} />
      </div>
      <div>
        <p className="font-bold text-xl">{contact.title}</p>
        {contact.description && <p className="text-sm opacity-80">{contact.description}</p>}
        {contact.address && <p className="text-xs opacity-70 mt-0.5">{contact.address}</p>}
      </div>
    </div>
    <div className="flex flex-col gap-2">
      {contact.phones.map((p) => (
        <a
          key={p.number}
          href={telHref(p.number)}
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-red-600 px-4 py-3 rounded-xl font-bold hover:bg-red-50 transition-colors"
        >
          <Phone size={16} />
          Llamar {p.label ? `${p.label} · ` : ''}{p.number}
        </a>
      ))}
    </div>
  </div>
);

export const EmergencyPage: React.FC<EmergencyPageProps> = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12 animate-fade-in">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium mb-6"
      >
        <ArrowLeft size={18} />
        Volver
      </button>

      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center shrink-0">
          <AlertCircle className="text-red-600" size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900">Emergencias en ruta</h1>
          <p className="text-gray-500 text-sm">Contactos de ayuda rápida · Ruta 68</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8">
        <p className="font-bold text-amber-800 mb-1">Importante</p>
        <p className="text-amber-700">
          Si es una emergencia grave, llama primero a Carabineros (133) o SAMU (131).
        </p>
      </div>

      {/* Números nacionales */}
      <div className="space-y-4 mb-8">
        {NATIONAL_CONTACTS.map((contact) => (
          <ContactCard
            key={contact.title}
            contact={contact}
            color="bg-red-50 border-red-200 text-red-700"
            Icon={NATIONAL_ICONS[contact.title] ?? Shield}
          />
        ))}
      </div>

      {/* Concesionaria + postes SOS */}
      <div className="mb-8">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">En la autopista</p>
        <ContactCard
          contact={ROUTE_68_CONCESSIONAIRE}
          color="bg-orange-50 border-orange-200 text-orange-700"
          Icon={AlertCircle}
        />
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <MapPin size={14} />
          <span>{ROUTE_68_SOS_NOTE}</span>
        </div>
      </div>

      {/* Asistencia particular */}
      <div className="mb-8">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Asistencia particular en ruta</p>
        <div className="space-y-4">
          {PRIVATE_ASSISTANCE.map((contact) => (
            <ContactCard
              key={contact.title}
              contact={contact}
              color="bg-gray-50 border-gray-200 text-gray-700"
              Icon={Wrench}
            />
          ))}
        </div>
      </div>

      {/* Por comuna — colapsable, secundario */}
      <div className="mb-8">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Por comuna</p>
        <div className="space-y-3">
          {EMERGENCY_COMUNAS.map((c) => (
            <details key={c.comuna} className="bg-white border border-gray-200 rounded-2xl overflow-hidden group">
              <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between font-bold text-gray-900">
                {c.comuna}
                <ChevronDown size={18} className="text-gray-400 transition-transform group-open:rotate-180" />
              </summary>
              <div className="px-5 pb-5 space-y-3">
                {c.contacts.map((contact) => (
                  <div key={contact.title} className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0">
                    <p className="font-semibold text-gray-800 text-sm">{contact.title}</p>
                    {contact.address && <p className="text-xs text-gray-400 mb-1">{contact.address}</p>}
                    <div className="flex flex-wrap gap-2">
                      {contact.phones.map((p) => (
                        <a
                          key={p.number}
                          href={telHref(p.number)}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-red-700"
                        >
                          <Phone size={13} /> {p.number}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Información verificada por nuestro equipo el {new Date(`${EMERGENCY_DATA_VERIFIED_AT}T00:00:00`).toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' })}. Cobertura actual: solo Ruta 68.
      </p>
    </div>
  );
};
